/**
 * Derives the "Disbursement" rail model for a TAS application's course-management
 * (`manage`) screen — a pure function over the application's data so it can be
 * unit tested without rendering. Mirrors the Figma "Disbursement Section V2" spec:
 * one status card per active payment method (Reimbursement OR Pre-Paid, plus
 * CourseCa$h™ when the program is a direct-pay/card program), each moving
 * through its own state machine.
 *
 * Reimbursement and Pre-Paid are mutually exclusive. The template timing flag
 * (`isPrepaidProgram`, i.e. `PROGRAM_PAYMENT === 'ON_ATTEND'`) is only the
 * pre-assistance prior: live CourseCa$h™ templates carry ON_ATTEND too, and an
 * ON_ATTEND application whose money actually moves as REIMBURSEMENT-manner
 * assistances is a reimbursement, not a prepayment — so once any reimbursement
 * assistance exists (`hasReimbursementAssistance`), it overrides the flag
 * (#10283: an all-reimbursement application rendered a "Pre-Paid" card).
 * CourseCa$h™ is independent/additive: a Type 4.3 mixed program can show it
 * alongside either of the other two.
 *
 * Whether CourseCa$h™ renders at all is the APPLICATION's payment election —
 * `paymentMethod` (`PAYMENT_TYPE_FOR_DIRECT_PAY` on the application's own
 * fields), the same gate every legacy surface uses
 * (calculated-application-total, editable-application-total, limits-summary).
 * The template's capability flag (`isDirectPayProgram`) is NOT enough: an
 * employee on a CC$-capable program who elected reimbursement has no card and
 * no CC$ money, but the template flag is still true — deriving from it split
 * a $25 all-reimbursement application into a $20 Reimbursement card and a
 * phantom $5 CourseCa$h™ card (#10392; the $5 being the tuition subtotal,
 * which `requestedCourseCashSubtotal` reports for ordinary reimbursement
 * applications too, `tas-application.ext.ts:592-601`). `courseCashReserved`/
 * `courseCashUsed > 0` is kept as a fallback so an already-issued card still
 * surfaces even if the application's election field has since changed —
 * `used` matters independently because `reserved` is the remaining balance
 * and reads 0 on a fully-spent card.
 *
 * Amount sourcing deliberately avoids the aggregate `requestedTotal`/`approvedTotal`
 * getters for the Reimbursement/Pre-Paid card once CourseCa$h™ is also present —
 * those aggregates fold the CC$-eligible tuition portion back in, which would
 * make the reimbursement amount indistinguishable from the card allocation.
 * `requestedNonCourseCashSubtotal` already nets that out on the requested side.
 * On the approved side, `preApprovedTotal`/`finalApprovedTotal` are already
 * reimbursement-only (confirmed via `editable-application-total.gts`'s
 * `reimbursementOnly` arg doc: they never include `CARD_*_APPROVED_TOTAL`) — but
 * which of the two to read depends on approval STAGE, not truthiness: `finalApprovedTotal`
 * legitimately reads a real `0` once final approval reduces reimbursement to
 * zero, and `0 || preApprovedTotal` would resurrect the stale pre-approved
 * figure instead of showing $0.00. Mirrors ManagePage's own `finalApprovedLabel` getter.
 *
 * `amounts.course_cash.reserved` is the CURRENT remaining balance, already net
 * of used/refunded funds — confirmed by `stripe-card-display.gts:190-192`'s
 * explicit backend-contract comment ("Backend returns the remaining available
 * amount"). So for CourseCa$h™: Remaining = `reserved` directly, and the
 * original/Approved total = `used + reserved` (adding back what's already been
 * spent) — NOT `reserved - used`, which would double-subtract. `reserved` can go
 * NEGATIVE, because covered merchant fees let a card spend past its allocation;
 * the displayed Remaining floors at 0 while the Approved reconstruction keeps the
 * raw value (see the CourseCa$h™ card below).
 *
 * TODO: `courseCashUseByDate` has no confirmed model field yet (same placeholder
 * noted in `attention-tasks.ts`) — passed through as optional.
 */

export type DisbursementCardType = 'reimbursement' | 'course-cash' | 'pre-paid';

export type DisbursementStatus =
  | 'requested'
  | 'pre-approved' // Reimbursement only — "course in progress"
  | 'pending' // Reimbursement + Pre-Paid — approved, not yet disbursed
  | 'paid' // Reimbursement + Pre-Paid
  | 'available' // CourseCa$h™ only — approved, reserved, not yet spent
  | 'active' // CourseCa$h™ only — spent at least once
  | 'deactivated'; // CourseCa$h™ only — card deactivated

export interface DisbursementPayment {
  /** Cents. */
  amount: number;
  /** ISO date of the payment; null while pending (nothing has settled yet). */
  date: string | null;
  /** Issued but not yet settled (assistance in PAYABLE). */
  pending: boolean;
}

export interface DisbursementCard {
  type: DisbursementCardType;
  status: DisbursementStatus;
  /** Cents. */
  requestedAmount: number;
  /** Cents — undefined at the `requested` status (nothing approved yet). */
  approvedAmount?: number;
  /** CourseCa$h™ only, `available`/`active` statuses. */
  useByDate?: string | null;
  /** CourseCa$h™ only, `active`/`deactivated` statuses. */
  usedAmount?: number;
  /** CourseCa$h™ only, `active` status. */
  remainingAmount?: number;
  /** Reimbursement/Pre-Paid only — one row per issued payment, so a second
   * disbursement is visible instead of silently folded into the aggregate
   * (#10284). Absent when no payment has been issued yet. */
  payments?: DisbursementPayment[];
}

export interface DisbursementInput {
  state: string;
  /** Type 2/4.2 programs have no separate courses-approval step — their first
   * submission enters PENDING_EVIDENCE_APPROVAL directly (mirrors the private
   * firstTimeSubmissionMap in tas-application.ext.ts), so that state must be
   * treated as "requested", not "approved", for those two types only. */
  typeClassification: string;
  isPrepaidProgram: boolean;
  /** The application's own money already moves as reimbursement (any
   * REIMBURSEMENT-manner assistance) — trumps `isPrepaidProgram`, which is
   * only the template's timing flag (see module doc / #10283). */
  hasReimbursementAssistance: boolean;
  /** Whether THIS application elected CourseCa$h™ as its payment method
   * (`application.paymentMethod === COURSE_CASH`) — the template's capability
   * flag alone must not surface the card (see module doc / #10392). */
  isCourseCashElected: boolean;
  requestedTotal: number;
  /** Pre-approval CC$-eligible (tuition) subtotal — populated regardless of approval stage. */
  requestedCourseCashSubtotal: number;
  /** Pre-approval non-CC$ (books/labs/software/fees, or full total on a non-CC$ program) subtotal. */
  requestedNonCourseCashSubtotal: number;
  /** Reimbursement-only — see module doc. */
  preApprovedTotal: number;
  /** Reimbursement-only — see module doc. */
  finalApprovedTotal: number;
  paidTotal: number;
  /** Current remaining CC$ balance (already net of used/refunded) — see module doc. */
  courseCashReserved: number;
  courseCashUsed: number;
  courseCashUseByDate?: string | null;
  /** Whether the application's active/primary PCS card has been deactivated. */
  pcsCardDeactivated: boolean;
  /** Issued reimbursement/pre-paid payments, one per money assistance
   * (SETTLED or PAYABLE) — attached to the primary card as its per-payment
   * rows (#10284). */
  payments?: DisbursementPayment[];
}

// Not yet approved — no approval amounts to show, whichever card.
const PRE_APPROVAL_STATES = new Set([
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
]);

// These two types have no courses-approval step — PENDING_EVIDENCE_APPROVAL
// is their first-submission state (tas-application.ext.ts's
// firstTimeSubmissionMap), so it means "just submitted", not "approved".
const EVIDENCE_APPROVAL_IS_FIRST_SUBMISSION = new Set(['TAS.ProgramType.2', 'TAS.ProgramType.4.2']);

// Evidence has actually been approved — only then can a reimbursement/pre-paid
// disbursement have moved past "course in progress" into pending/paid.
const POST_FINAL_APPROVAL_STATES = new Set([
  'TAS.ApplicationState.EVIDENCE_APPROVED',
  'TAS.ApplicationState.FULFILLED',
]);

export function deriveDisbursement(app: DisbursementInput): DisbursementCard[] {
  const cards: DisbursementCard[] = [];

  const isRequested =
    PRE_APPROVAL_STATES.has(app.state) ||
    (app.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL' &&
      EVIDENCE_APPROVAL_IS_FIRST_SUBMISSION.has(app.typeClassification));
  const isFinalApproved = POST_FINAL_APPROVAL_STATES.has(app.state);
  // Election first, issued-card money as the fallback — `used` alongside
  // `reserved` because a fully-spent card has reserved at 0 (module doc).
  const hasCourseCash =
    app.isCourseCashElected || app.courseCashReserved > 0 || app.courseCashUsed > 0;
  const nonCourseCashRequested = hasCourseCash
    ? app.requestedNonCourseCashSubtotal
    : app.requestedTotal;
  // Selected by approval STAGE, not truthiness — finalApprovedTotal can
  // legitimately be a real 0 once final approval reduces reimbursement to
  // zero, and `0 || preApprovedTotal` would resurrect the stale pre-approved
  // figure instead. Mirrors ManagePage's own finalApprovedLabel getter.
  const reimbursementApprovedAmount = isFinalApproved
    ? app.finalApprovedTotal
    : app.preApprovedTotal;

  // Reimbursement / Pre-Paid — mutually exclusive by program payment timing.
  // (nonCourseCashRequested already equals requestedTotal whenever there's no
  // CourseCash portion, so this alone also correctly hides the card when
  // truly nothing has been requested at all.)
  if (nonCourseCashRequested > 0) {
    // Template timing is only the prior; actual reimbursement money wins (#10283).
    const primaryType: DisbursementCardType =
      app.isPrepaidProgram && !app.hasReimbursementAssistance ? 'pre-paid' : 'reimbursement';
    let status: DisbursementStatus;
    if (isRequested) {
      status = 'requested';
    } else if (app.paidTotal > 0) {
      // Checked before the approval-stage branch below: Pre-Paid assistance
      // settles on ATTEND, well before evidence approval, so gating "paid" on
      // isFinalApproved would leave a settled Pre-Paid card stuck on "pending".
      status = 'paid';
    } else if (primaryType === 'reimbursement' && !isFinalApproved) {
      // Pre-Paid has no separate "course in progress" chip in the Figma spec —
      // it goes straight to Pending once approved.
      status = 'pre-approved';
    } else {
      status = 'pending';
    }

    cards.push({
      type: primaryType,
      status,
      requestedAmount: nonCourseCashRequested,
      approvedAmount: isRequested ? undefined : reimbursementApprovedAmount,
      payments: app.payments?.length ? app.payments : undefined,
    });
  }

  // CourseCa$h™ — independent of the above; shows whenever this application
  // elected the card (or one is already funded), regardless of which
  // Reimbursement/Pre-Paid card (if any) also renders.
  if (hasCourseCash) {
    let status: DisbursementStatus;
    if (isRequested) {
      status = 'requested';
    } else if (app.pcsCardDeactivated) {
      status = 'deactivated';
    } else if (app.courseCashUsed > 0) {
      status = 'active';
    } else {
      status = 'available';
    }

    // reserved is the current remaining balance, not the original allocation —
    // add back what's already been used to get the original approved total.
    const courseCashApprovedAmount = app.courseCashUsed + app.courseCashReserved;

    cards.push({
      type: 'course-cash',
      status,
      requestedAmount: app.requestedCourseCashSubtotal,
      approvedAmount: isRequested ? undefined : courseCashApprovedAmount,
      useByDate:
        status === 'available' || status === 'active' ? app.courseCashUseByDate : undefined,
      usedAmount: status === 'active' || status === 'deactivated' ? app.courseCashUsed : undefined,
      // Floored at 0: merchant fees are covered ON TOP of the allocation (up to
      // 4%), so a card can legitimately spend more than was reserved for it and
      // `reserved` then arrives NEGATIVE — a $3.69 allocation with $3.83 spent
      // reports -14, which rendered as "-$0.14 remaining" while the card-details
      // panel one column over said $0.00 (#10426). Nothing is left to
      // spend either way, so 0 is the honest figure, and it matches both
      // card-details-tab.gts's `cardRemainingAmountCents` and the model's own
      // `courseCashAvailableBalance`, which already clamp the same value.
      //
      // `courseCashApprovedAmount` above deliberately keeps the RAW value:
      // `used + reserved` is precisely what recovers the original allocation
      // ($3.83 + -$0.14 = $3.69), and clamping there would inflate Approved to
      // the overspent total.
      remainingAmount: status === 'active' ? Math.max(0, app.courseCashReserved) : undefined,
    });
  }

  return cards;
}
